// R7.1 — §6b, THE SPECIALTY CONVENTION PANEL (spec §5).
//
// IT RENDERS IN THE EXISTING SPECIALTY SLOT, inside `SpecialtyOverlayPanel`, above the legacy/overlay content that
// slot already carries. Nothing else about that slot moves: the legacy read flow, the generic-mode notice and the
// expired-profile notice are untouched, which is the owner's frozen-surface rule applied literally.
//
// FIXED SECTION ORDER, ALWAYS THREE (spec §5): A. Specialty convention → B. Published surveys → C. Program
// descriptions (lower bound). B and C ship in their EMPTY state by ratified decision (Q4, Q3) and still render —
// their registered empty string says why, because silence would leave a reader to guess whether the product
// looked and found nothing or never looked.
//
// WHAT THIS COMPONENT MAY NEVER DO, each of which is a §6 test target: TELL THE USER a count, total anything,
// re-order rows by status, colour by status, put a number on a Not-documented card, or say anything about
// programs. There is no comparator in this file.
//
// ONE CLAUSE OF THAT BANNER CHANGED, DELIBERATELY (owner directive 2026-08-23, verbatim: "giving users feedback on
// dimensions will not help but rather overwhelm them… decide extent/order based on dimension coverage, but raw
// data is not helpful as the synthesis is"). It used to read "count rows … collapse by status", full stop. The
// reason for that rule was that a count on this surface becomes a score, and a status-driven collapse becomes a
// ranking. Both reasons still hold and are still enforced:
//
//   * NO COUNT AND NO FRACTION IS EVER RENDERED. `coverageShape` below tallies statuses to CHOOSE a registered
//     template and throws the tally away; `n` never reaches a slot. The only number this panel has ever printed
//     is the DOCUMENTED card's own `{n}` entries and the per-row omission scope, both unchanged.
//   * NO ROW IS RE-ORDERED. The verdict does not sort. The problem → what-to-do list is a FILTER over the rows in
//     PAYLOAD (ratified set) order — `rows.filter(...)`, never `rows.sort(...)` — so T-CONV-5's "a comparator
//     reading class or count fails" is as true as it was.
//
// What the owner overrode is the third clause: the full per-area list is now CLOSED BY DEFAULT behind a
// disclosure, and the rows that name something the CV does not yet describe are lifted out in front of it. That is
// a change to what is VISIBLE FIRST, not to what is said, what is counted, or what order anything is in.
//
// THE CLAIM AND THE FOOTER ARE MANDATORY and are the first and last things in the panel.
import type { RubricReadPublicV1 } from '../../../workers/src/lib/rubricReadPublic';
import type { ConventionRow } from '../../../workers/src/lib/specialtyConvention';
import { Copy, EngineRendered, EvidenceReveal, LayerHeading, layerHeadingId } from './renderContent';
import { resolveEvidence, type EvidenceIndex } from './evidenceIndex';
import { scanConventionText } from '../../../workers/src/lib/conventionBannedOutput';
import { SPECIALTY_REGISTRY } from '../../../workers/src/lib/specialtyRegistry';
import {
  BODY_STYLE, CARD_STYLE, FACTS_DETAILS_STYLE, FACTS_SUMMARY_STYLE, MUTED_STYLE, NOTICE_STYLE, SECTION_STYLE,
} from './styles';

type Panel = NonNullable<RubricReadPublicV1['convention']>;

/**
 * ROW-GRANULARITY FAIL-CLOSED (T-CONV-1, reusing the overlay's R5.4 ruling verbatim).
 *
 * A violation in ANY of the row's system-authored strings takes the WHOLE row and stops there — the claim, the
 * hedges, the footer and every sibling row still render. Half a card is not a safer card: an area label with its
 * verdict removed, or a verdict with its area removed, states something nobody authored.
 *
 * The scan is a PRE-PASS, before any part of the row is emitted, so a violation in the last string cannot leave
 * the first three on screen.
 */
export function conventionRowRenders(row: ConventionRow): boolean {
  return (
    scanConventionText(row.area_label).ok
    && scanConventionText(row.card.text, row.card.copy_string_id).ok
    && scanConventionText(row.source.text, row.source.copy_string_id).ok
    && (row.recover === null || scanConventionText(row.recover.text, row.recover.copy_string_id).ok)
    && scanConventionText(row.ratification_doc).ok
  );
}

/**
 * R7.3 — does section A of the convention panel above have at least one renderable row?
 *
 * Reused by `SpecialtyOverlayPanel` to decide whether the overlay's generic-mode notice would contradict a
 * convention panel that just rendered rows above it. Goes through the same `conventionRowRenders` fail-closed
 * scan the panel itself uses for section A, so a payload full of rows that are all individually scan-refused is
 * correctly treated as "no rows" here too — never duplicated logic, the same predicate.
 */
export function conventionPanelHasRows(convention: Panel | null): boolean {
  if (convention === null) return false;
  return convention.convention.rows.filter(conventionRowRenders).length > 0;
}

/**
 * THE COVERAGE SHAPE — a TEMPLATE SELECTOR over facts, and the only thing on this surface that reads more than
 * one row at a time.
 *
 * It returns a NAME, not a number. The statuses are tallied here and the tally is discarded: what leaves this
 * function is one of four registered template ids, so there is no path from a count to the screen. A row the
 * scan refused never reaches here (the caller filters first), so the shape describes what the user can see.
 *
 *   every renderable row `unbound`                          → `-UNREAD` (nothing was read; no verdict is honest)
 *   ANY row `unbound`, and something was read                → `-PARTIAL-READ`  ← the exhaustive-claim guard
 *   all rows `documented`                                   → `-ALL`
 *   ≥1 `documented` and ≥1 `partial`/`not_documented`       → `-MIXED`
 *   no `documented`, ≥1 `partial`/`not_documented`          → `-NONE`
 *
 * `unbound` rows are IGNORED by the documented/undocumented arithmetic rather than counted as either: an area this
 * version could not read from the ledger is not evidence that the CV documents it, and it is not evidence that
 * the CV is silent on it.
 *
 * BUT IGNORING THEM CANNOT BE ALLOWED TO PRODUCE AN EXHAUSTIVE CLAIM (Sol review, 2026-08-23). `-ALL` and `-NONE`
 * are statements about the WHOLE ratified set — "every area these standards name", "the areas these standards
 * name" — so one documented row beside forty-two unbound ones selected `-ALL` and told the applicant their CV
 * covered every area. False, and about their CV. Whenever ANY row is unbound and anything at all was read,
 * `-PARTIAL-READ` fires instead: it states the limitation and claims nothing in either direction, and the rows
 * that COULD be read are still listed under it. `-MIXED` is not reused for this — it asserts there ARE
 * undocumented areas below, which is not established when the undocumented tally is zero.
 */
export type ConventionVerdictId =
  | 'CS-CONV-VERDICT-ALL' | 'CS-CONV-VERDICT-MIXED' | 'CS-CONV-VERDICT-NONE' | 'CS-CONV-VERDICT-UNREAD'
  | 'CS-CONV-VERDICT-PARTIAL-READ';

export function conventionVerdictId(rows: readonly ConventionRow[]): ConventionVerdictId | null {
  if (rows.length === 0) return null;
  let documented = 0;
  let undocumented = 0;
  for (const r of rows) {
    if (r.status === 'documented') documented += 1;
    else if (r.status === 'partial' || r.status === 'not_documented') undocumented += 1;
  }
  const unbound = rows.length - documented - undocumented;
  if (documented === 0 && undocumented === 0) return 'CS-CONV-VERDICT-UNREAD';
  // THE GUARD, BEFORE THE EXHAUSTIVE PAIR: an unread area makes "every area" and "none of the areas" unprovable.
  if (unbound > 0) return 'CS-CONV-VERDICT-PARTIAL-READ';
  if (undocumented === 0) return 'CS-CONV-VERDICT-ALL';
  if (documented === 0) return 'CS-CONV-VERDICT-NONE';
  return 'CS-CONV-VERDICT-MIXED';
}

/**
 * THE PROBLEM → WHAT-TO-DO ROWS (owner: "where is the problem, what to do/how to fix it").
 *
 * A FILTER, IN PAYLOAD ORDER. The `partial` and `not_documented` rows, exactly as the payload ordered them. This
 * is not a ranking and not a re-sort: `filter` preserves the ratified set order by construction, and there is no
 * comparator here to read a class or a count (T-CONV-5).
 *
 * Each line is the two things the owner asked for and nothing else: the AREA (what is not documented) and the
 * row's own `recover` string (what you could say if you have it). The `recover` string already IS that sentence —
 * the ratified `CS-CONV-RECOVER` question the row carries — so this reuses it rather than authoring a second one.
 * A row with no `recover` (impossible for these two statuses today, but the type allows it) is skipped: an area
 * named with nothing to do about it is the "raw data" this restructure exists to remove.
 */
export function conventionTodoRows(rows: readonly ConventionRow[]): ConventionRow[] {
  return rows.filter((r) => (r.status === 'partial' || r.status === 'not_documented') && r.recover !== null);
}

function ConventionTodo({ row }: { row: ConventionRow }) {
  return (
    <li style={CARD_STYLE} data-signal-id={row.signal_id} data-convention-status={row.status}>
      {/* WHAT IS NOT DOCUMENTED — the area in the ratified set's own words. Same string as the row below; no
          badge, no colour, no number. */}
      <p style={BODY_STYLE} data-testid="rubric-convention-todo-area">{row.area_label}</p>
      {/* WHAT YOU COULD SAY IF YOU HAVE IT — the row's own registered recover question, verbatim. */}
      {row.recover && (
        <p style={BODY_STYLE} data-testid="rubric-convention-todo-recover">
          <EngineRendered value={row.recover} />
        </p>
      )}
    </li>
  );
}

function ConventionCard({ row, evidence }: { row: ConventionRow; evidence: EvidenceIndex }) {
  return (
    <li style={CARD_STYLE} data-signal-id={row.signal_id} data-convention-status={row.status}>
      {/* The area, in the ratified set's own words. Uniform weight — no badge, no chip, no colour by status. */}
      <p style={BODY_STYLE} data-testid="rubric-convention-area">{row.area_label}</p>
      <p style={BODY_STYLE} data-testid="rubric-convention-card">
        <EngineRendered value={row.card} />
      </p>
      {/* The recover question, on PARTIAL and NOT-DOCUMENTED rows: what the applicant could say if the experience
          exists and the CV simply does not describe it. Never an instruction to go DO something. */}
      {row.recover && (
        <p style={BODY_STYLE} data-testid="rubric-convention-recover">
          <EngineRendered value={row.recover} />
        </p>
      )}
      <EvidenceReveal evidence={resolveEvidence(evidence, row.evidence_fact_ids)} />
      {/* ============================================================================================================ *
          A.5-P.22 — THE ROW'S PROVENANCE, BEHIND ONE DISCLOSURE (owner directive 2026-08-23: "display what is
          relevant to the user and not how we derived it or the source").

          EVERY ROW STILL CITES ITS OWN STANDARD (spec §1) and the omission scope still lives on the row and nowhere
          else (spec §4, Sol edit c). NOTHING IS DELETED and no string is edited — `CS-CONV-SOURCE` and
          `CS-OMISSION-SCOPE` render exactly as they did, through the same components. What changed is that they no
          longer render INLINE: a 43-area specialty printed 43 "Source: …" lines and 43 scope sentences between the
          reader and the thing they came to fix. They now sit behind ONE `<details>` per row, labelled by the
          registered `CS-CONV-SHOW-SOURCE` — one control, not the two adjacent "Shown from your CV" summaries the
          split version produced. `EvidenceReveal` above stays its own disclosure: that one shows the applicant
          THEIR OWN WORDS, which is content, not derivation.
          ============================================================================================================ */}
      <details className="rubric-evidence" data-testid="rubric-convention-source-details">
        <summary style={MUTED_STYLE} data-testid="rubric-convention-source-toggle">
          <Copy id="CS-CONV-SHOW-SOURCE" />
        </summary>
        <p style={MUTED_STYLE} data-testid="rubric-convention-source">
          <EngineRendered value={row.source} />
        </p>
        {row.status === 'not_documented' && row.search_scope && row.search_scope.n_assessed > 0 && (
          <p style={MUTED_STYLE} data-testid="rubric-convention-omission-scope">
            <Copy id="CS-OMISSION-SCOPE" slots={{ n: String(row.search_scope.n_assessed) }} />
          </p>
        )}
      </details>
    </li>
  );
}

/**
 * R7.2 — THE §6b SIGNALS RE-EXTRACTION STATE, as this panel needs to render it.
 *
 * The panel does not run anything and holds no state: `RubricReadView` owns the offer, the auto-run and the
 * failure memory (a panel that could start an LLM call from its own render would be a second decision-maker about
 * when the applicant's quota is spent). This is the two things the panel can honestly SHOW — that a pass is
 * running, and that one failed and can be retried — and nothing else.
 */
export interface ConventionReextractState {
  /** A pass is in flight: the announced wait renders, in this panel's own slot. */
  busy: boolean;
  /** The last attempt failed. The retry CTA renders; the rows below still render exactly as they did. */
  failed: boolean;
  /** The retry. Absent when there is nothing to retry (no offer on this read). */
  onRetry?: () => void;
}

/**
 * THE WAIT AND THE RETRY, in the convention panel's slot and nowhere else.
 *
 * Exported so the slot can render it even when there is no `convention` payload to hang it under (a read computed
 * before R7.1, or the layer off for this read but on for the ledger) — an invisible failure with no retry
 * affordance is exactly the state this whole change exists to end.
 */
export function ConventionReextractNotice({ state }: { state: ConventionReextractState | null }) {
  if (state === null) return null;
  return (
    <>
      {state.busy && (
        <p
          style={NOTICE_STYLE}
          role="status"
          aria-busy="true"
          data-testid="rubric-convention-reextract-wait"
        >
          <Copy id="CS-CONV-REEXTRACT-WAIT" />
        </p>
      )}
      {!state.busy && state.failed && state.onRetry && (
        <button
          type="button"
          className="btn btn-secondary"
          onClick={state.onRetry}
          data-testid="rubric-convention-reextract-cta"
        >
          <Copy id="CS-CONV-REEXTRACT-CTA" />
        </button>
      )}
    </>
  );
}

export function SpecialtyConventionPanel({
  convention,
  evidence,
  reextract = null,
}: {
  convention: Panel | null;
  evidence: EvidenceIndex;
  /** R7.2 — the re-extraction wait / retry, rendered under this panel's claim. `null` on every ordinary read. */
  reextract?: ConventionReextractState | null;
}) {
  if (convention === null) return null;
  const rows = convention.convention.rows.filter(conventionRowRenders);
  // A section whose every row was refused by the scan is an EMPTY section, and an empty section says so — the
  // same rule that governs an unreviewed one. It never falls silent.
  const conventionEmptyId = rows.length === 0
    ? (convention.convention.empty_copy_string_id ?? 'CS-CONV-EMPTY-CONVENTION')
    : null;
  // THE SYNTHESIS, over the rows the reader can actually see (scan-refused rows are already gone).
  const verdictId = conventionVerdictId(rows);
  const todo = conventionTodoRows(rows);
  // The registry's own display name, and FAIL-CLOSED WITHOUT IT: an unregistered specialty would render "a
  // documented  CV" — a sentence with a hole in it, which is worse than no sentence. The rows below still render,
  // so nothing is lost except the summary that could not be written honestly.
  const specialtyName = SPECIALTY_REGISTRY[convention.specialty]?.display_name ?? '';

  return (
    <section
      aria-labelledby={layerHeadingId('CS-SECTION-CONVENTION')}
      style={SECTION_STYLE}
      data-testid="rubric-layer-convention"
    >
      <LayerHeading id="CS-SECTION-CONVENTION" />

      {/* R7.2 — the re-extraction state, directly under the claim and ABOVE the rows it is about to change.
          It never replaces the rows: whatever the ledger currently binds keeps rendering while the pass runs
          (the read is only replaced when the refresh lands), so nothing on screen is taken away to show a wait. */}
      <ConventionReextractNotice state={reextract} />

      {/* A. SPECIALTY CONVENTION.
          THE SYNTHESIZED VERDICT, DIRECTLY UNDER THE CLAIM (owner directive 2026-08-23: "raw data is not helpful
          as the synthesis is"). Spec §0/§5 pin the CLAIM as the first thing in the panel and the footer as the
          last, and that is unchanged — so the verdict goes IMMEDIATELY BELOW the claim rather than above it, which
          is the only placement that satisfies both the spec's ordering and the owner's "lead with the answer".
          The claim says what this layer is; the verdict answers the question the applicant came with. */}
      {verdictId && specialtyName.length > 0 && (
        <p style={BODY_STYLE} data-testid="rubric-convention-verdict">
          {/* `{specialty}` is the registry's own `display_name` — `copy` class, scanned like every other slot. */}
          <Copy id={verdictId} slots={{ specialty: specialtyName }} />
        </p>
      )}

      {/* THE PROBLEM → WHAT-TO-DO LIST: what your CV does not yet describe, and what you could say if you have it.
          Payload order, filtered, never re-ranked. */}
      {todo.length > 0 && (
        <>
          <p style={NOTICE_STYLE} data-testid="rubric-convention-todo-heading">
            <Copy id="CS-CONV-TODO-HEADING" />
          </p>
          <ol style={{ listStyle: 'none', margin: 0, padding: 0 }} data-testid="rubric-convention-todo">
            {todo.map((row) => <ConventionTodo key={row.signal_id} row={row} />)}
          </ol>
        </>
      )}

      {conventionEmptyId ? (
        <>
          {/* An EMPTY section keeps its hedge and its registered "found nothing" string in the open, exactly as
              before: there is no full list to disclose, so there is nothing to put behind a disclosure. */}
          <p style={NOTICE_STYLE} data-testid="rubric-convention-hedge">
            <Copy id={convention.convention.hedge_copy_string_id} />
          </p>
          <p style={NOTICE_STYLE} data-testid="rubric-convention-empty">
            <Copy id={conventionEmptyId} />
          </p>
        </>
      ) : (
        /* THE FULL PER-AREA LIST, CLOSED BY DEFAULT. The rows are BYTE-IDENTICAL to what they were — same markup,
           same citations, same evidence reveals, same `conventionRowRenders` fail-closed pre-pass. Only their
           default visibility moved: `<details>` with no `open`, so the reader who wants the areas we checked can
           have all of them, and the reader who wants the answer is not made to read 43 rows to find it. */
        <details style={FACTS_DETAILS_STYLE} data-testid="rubric-convention-areas-details">
          <summary style={FACTS_SUMMARY_STYLE} data-testid="rubric-convention-areas-toggle">
            <Copy id="CS-CONV-SHOW-AREAS" />
          </summary>
          <p style={NOTICE_STYLE} data-testid="rubric-convention-hedge">
            <Copy id={convention.convention.hedge_copy_string_id} />
          </p>
          <ol style={{ listStyle: 'none', margin: 0, padding: 0 }} data-testid="rubric-convention-rows">
            {/* PAYLOAD ORDER, WHICH IS THE RATIFIED SET ORDER. This component sorts nothing (T-CONV-5). */}
            {rows.map((row) => (
              <ConventionCard key={row.signal_id} row={row} evidence={evidence} />
            ))}
          </ol>
        </details>
      )}

      {/* B. PUBLISHED SURVEYS — Sol + owner, R7.3: an EMPTY section renders NOTHING (no heading, no hedge, no
          empty string), rather than the two permanently-empty panels the always-render version produced. Section
          A's own empty-state behaviour (a rendered "found nothing" string) is untouched — only B and C, which ship
          empty in every release today (decision Q4 for B, Q3 for C), are hidden when they have no content. When
          this section ever ships rows, the heading + hedge + rows render exactly as section A's do. */}
      {convention.surveys.rows.length > 0 && (
        <>
          <LayerHeading id={convention.surveys.heading_copy_string_id} />
          <p style={NOTICE_STYLE} data-testid="rubric-convention-survey-hedge">
            <Copy id={convention.surveys.hedge_copy_string_id} />
          </p>
        </>
      )}

      {/* C. PROGRAM DESCRIPTIONS (LOWER BOUND) — same rule as section B: hidden entirely when empty. */}
      {convention.corpus.rows.length > 0 && (
        <>
          <LayerHeading id={convention.corpus.heading_copy_string_id} />
          <p style={NOTICE_STYLE} data-testid="rubric-convention-corpus-hedge">
            <Copy id={convention.corpus.hedge_copy_string_id} />
          </p>
        </>
      )}

      {/* ========================================================================================================== *
          A.5-P.22 — THE PANEL'S METHODOLOGY, BEHIND ONE DISCLOSURE AT THE FOOT.

          The mandatory claim (spec §0) and the mandatory footer are BOTH statements about this layer rather than
          about the applicant's CV — what it is, and what it is not. They used to bracket the panel, so the reader
          met a paragraph of epistemics before the verdict and another after the rows. Owner, 2026-08-23: "it sounds
          like you are justifying your approach to a user."

          BOTH STILL RENDER, through `Copy`, from the payload's own `claim_copy_string_id` /
          `footer_copy_string_id` — the §12.6 allowlist is keyed by ID and is untouched, and the §6b banned scan
          still sees exactly the ids it saw before. Their SPEC ORDER is preserved inside the disclosure: claim
          first, footer last. Only the default visibility moved, and only to the foot of the panel, where a reader
          who wants the basis can open it in one click.
          ========================================================================================================== */}
      <details style={FACTS_DETAILS_STYLE} data-testid="rubric-convention-about-details">
        <summary style={FACTS_SUMMARY_STYLE} data-testid="rubric-convention-about-toggle">
          <Copy id="CS-CONV-ABOUT-TOGGLE" />
        </summary>
        {/* MANDATORY CLAIM — FIRST inside the disclosure, as spec §0 orders it. */}
        <p style={NOTICE_STYLE} data-testid="rubric-convention-claim">
          <Copy id={convention.claim_copy_string_id} />
        </p>
        {/* MANDATORY FOOTER, LAST. */}
        <p style={NOTICE_STYLE} data-testid="rubric-convention-footer">
          <Copy id={convention.footer_copy_string_id} />
        </p>
      </details>
    </section>
  );
}
